<template>
  <div class="message-item" :class="msg.role">
    <div class="message-header">
      <el-tag :type="msg.role === 'user' ? 'primary' : 'success'" size="small" effect="plain">
        {{ msg.role === 'user' ? '我' : 'AI' }}
      </el-tag>
      <span class="message-time">{{ msg.createTime }}</span>
    </div>

    <div v-if="msg.role === 'user'" class="message-content">
      <!-- 多图：历史还原走 msg.images（后端落盘的 /profile 相对路径，vite/nginx 已单独代理 /profile 直通后端）；
           本次会话内刚发的图仍是 data URL，走下面的单图分支即时回显 -->
      <div v-if="msg.images && msg.images.length" class="user-message-images">
        <el-image
          v-for="(imgUrl, imgIdx) in msg.images"
          :key="imgIdx"
          :src="imgUrl"
          :preview-src-list="msg.images"
          :initial-index="imgIdx"
          fit="cover"
          class="user-message-image is-grid"
          preview-teleported
        />
      </div>
      <img v-else-if="msg.image" :src="msg.image" class="user-message-image" alt="上传图片" />
      <span v-if="msg.content && msg.content !== '[Image]'">{{ msg.content }}</span>
    </div>

    <div v-else class="message-content markdown-body">
      <!-- 回合卡片：一条回答 = 一个卡片（对齐 Cursor / Qoder 的过程回馈节奏）。
           后端 14 个 PipelineStage 归并为 5 个动作：理解问题 / 检索知识 / 调用工具 / 生成回答 / 收尾。
           · 进行中：自动展开，逐行显示当前在做什么（含逐秒计时）
           · 完成后：收成一行摘要（"已完成 · 5 个动作 · 共 22.4s"），点击可展开
           工具调用与推理过程挂在对应动作下，不再与阶段时间线并列成两条。 -->
      <div v-if="turnActions.length" class="turn-card">
        <div class="turn-card-head" @click="$emit('toggleTurnCard', msg)">
          <el-icon class="turn-head-icon" :class="{ 'is-running': turnRunning, 'is-fail': turnFailed }">
            <Loading v-if="turnRunning" />
            <CircleCloseFilled v-else-if="turnFailed" />
            <CircleCheckFilled v-else />
          </el-icon>
          <span class="turn-head-text">
            {{ turnCardSummary(msg) }}
            <span v-if="turnRunning && turnElapsed" class="turn-head-elapsed"> · {{ turnElapsed }}</span>
          </span>
          <el-icon class="turn-head-arrow" :class="{ 'is-expanded': turnExpanded }"><ArrowRight /></el-icon>
        </div>
        <div v-show="turnExpanded" class="turn-card-body">
          <div
            v-for="act in turnActions"
            :key="act.key"
            class="turn-action"
            :class="['is-' + act.status, { 'is-tools': act.key === 'tools' }]"
          >
            <span class="turn-dot"></span>
            <span class="turn-title">{{ act.title }}</span>
            <span class="turn-summary">{{ act.summary }}</span>
            <span v-if="act.durationMs > 0" class="turn-dur">{{ (act.durationMs / 1000).toFixed(1) }}s</span>
            <!-- 动作下的原始步骤（阶段 / 工具调用），展开到这一层才看细节 -->
            <div v-if="act.steps.length > 1 || act.key === 'tools'" class="turn-steps">
              <template v-for="st in act.steps" :key="st.key">
                <div
                  class="turn-step"
                  :class="['is-' + st.status, { 'is-clickable': st.kind === 'tool' }]"
                  @click="st.kind === 'tool' && $emit('toggleToolNode', msg, st.tool._idx)"
                >
                  <el-icon v-if="st.status === 'running'" class="ts-icon is-pending"><Loading /></el-icon>
                  <el-icon v-else-if="st.status === 'error'" class="ts-icon is-fail"><CircleCloseFilled /></el-icon>
                  <span v-else class="ts-icon-dot"></span>
                  <span class="ts-label">{{ st.label }}</span>
                  <span v-if="st.detail" class="ts-detail">{{ st.detail }}</span>
                  <span v-if="st.durationMs > 0" class="ts-dur">{{ st.durationMs }}ms</span>
                </div>
                <!-- 工具明细：参数 / 当时推理 / 结果（复用既有展开态 tool._expanded） -->
                <div v-if="st.kind === 'tool' && st.tool._expanded && !st.tool.pending" class="tf-detail">
                  <div class="flow-block">
                    <span class="flow-block-label">参数</span>
                    <pre class="flow-block-code">{{ formatToolArguments(st.tool.arguments) }}</pre>
                  </div>
                  <div v-if="thinkingEnabled && st.tool.reasoning" class="flow-block">
                    <span class="flow-block-label">当时推理</span>
                    <pre class="flow-block-code">{{ st.tool.reasoning }}</pre>
                  </div>
                  <div class="flow-block">
                    <span class="flow-block-label">结果</span>
                    <pre class="flow-block-code">{{ formatToolResult(st.tool.result) }}</pre>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <!-- 整体推理过程（受深度思考开关控制）：作为独立折叠块，不混进动作行 -->
          <div v-if="thinkingEnabled && msg.reasoning" class="loop-reasoning">
            <div class="loop-reasoning-head" @click="$emit('toggleReasoning', msg)">
              <span class="loop-reasoning-label">推理过程</span>
              <el-icon class="loop-reasoning-arrow" :class="{ 'is-expanded': msg._reasoningExpanded }"><ArrowRight /></el-icon>
            </div>
            <div v-show="msg._reasoningExpanded" class="loop-reasoning-text">{{ msg.reasoning }}</div>
          </div>
          <!-- 进行中的工具调用兜底提示（仅在无结构化进行中节点时显示，避免与动作行重复） -->
          <div
            v-if="loading && msg.progress && msg.progress.nodeName === '工具调用' && !(msg.toolChain && msg.toolChain.some(tc => tc.pending))"
            class="tool-chain-pending"
          >
            <el-icon class="progress-icon"><Loading /></el-icon>
            <span class="tool-chain-pending-text">{{ msg.progress.detail || '工具执行中…' }}</span>
          </div>
        </div>
      </div>

      <!-- 后端 node_message 下发的整段告知（如知识库拒答说明）：必须直接可见，
           不能只留在过程面板里——否则拒答场景用户看到的是空白回答 -->
      <div v-if="visibleNotices.length" class="msg-notices">
        <div v-for="(n, ni) in visibleNotices" :key="ni" class="msg-notice">{{ n }}</div>
      </div>

      <!-- 等待首个 token：回合卡片已经给出了具体动作行，这里只在没有任何过程信息时兜底 -->
      <div v-if="!msg.content && loading && !turnActions.length && !(thinkingEnabled && msg.reasoning)" class="typing-indicator-wrapper">
        <div v-if="msg.progress" class="progress-info">
          <el-icon class="progress-icon"><Loading /></el-icon>
          <span class="progress-node">{{ msg.progress.nodeName }}</span>
          <span v-if="msg.progress.detail" class="progress-detail">{{ msg.progress.detail }}</span>
        </div>
        <div v-else class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <template v-else>
        <!-- Web search results (Doubao-style) - before content, collapsed by default -->
        <div v-if="msg.webSearchResults && msg.webSearchResults.length > 0" class="web-search-section">
          <div class="web-search-summary" @click="$emit('toggleWebSearch', msg)">
            <span class="web-search-keyword">搜索 1 个关键词，参考 {{ msg.webSearchResults.length }} 篇资料</span>
            <span class="web-search-toggle" :class="{ 'is-expanded': isWebSearchExpanded(msg) }">
              <svg width="12" height="12" viewBox="0 0 12 12"><polyline points="4,2 8,6 4,10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </div>
          <transition name="web-search-expand">
            <div v-show="isWebSearchExpanded(msg)" class="web-search-details">
              <div class="web-search-query">"{{ getWebSearchQuery(msg) }}"</div>
              <div class="web-search-refs">
                <div v-for="(item, idx) in msg.webSearchResults" :key="idx" class="web-search-ref-item">
                  <a v-if="item.url" :href="item.url" target="_blank" rel="noopener" class="web-search-ref-link">
                    <span class="web-search-ref-index">{{ idx + 1 }}.</span>
                    {{ item.title || item.url }}
                  </a>
                  <span v-else class="web-search-ref-link">
                    <span class="web-search-ref-index">{{ idx + 1 }}.</span>
                    {{ item.title }}
                  </span>
                </div>
              </div>
            </div>
          </transition>
          <div class="web-search-divider"></div>
        </div>
        <!-- Content（contentEl：句级朗读高亮的 DOM 宿主） -->
        <div ref="contentEl" v-html="renderMessageHtml(msg)"></div>
      </template>

      <!-- 模糊匹配澄清卡片（历史消息回看用；最新一轮的澄清由底部 V2 浮窗接管，此处隐藏） -->
      <div v-if="msg.clarification && !hideClarify" class="clarification-card">
        <div class="clarification-message">{{ msg.clarification.message }}</div>
        <div v-if="msg.clarification.options && msg.clarification.options.length > 0" class="clarification-options">
          <div
            v-for="(opt, oi) in msg.clarification.options"
            :key="oi"
            class="clarification-option"
            @click="$emit('clarify', msg, opt)"
          >
            <el-icon><ChatDotRound /></el-icon>
            <span>{{ opt.label }}</span>
          </div>
        </div>
        <div class="clarification-input-row">
          <el-input
            v-model="msg.clarificationDraft"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 5 }"
            resize="none"
            placeholder="补充描述您的问题，然后重新提问..."
            @keydown.enter.exact.prevent="$emit('clarify', msg)"
          />
          <el-button
            type="primary"
            :disabled="!(msg.clarificationDraft || '').trim()"
            @click="$emit('clarify', msg)"
          >
            <el-icon><Promotion /></el-icon>
            重新提问
          </el-button>
        </div>
      </div>
    </div>

    <!-- Report card display -->
    <div v-if="msg.role === 'assistant' && !loading && getReportCards(msg).length > 0" class="report-cards-section">
      <div v-for="(card, cardIndex) in getReportCards(msg)" :key="cardIndex" class="report-card" @click="openReportUrl(card.url)">
        <div class="report-card-body">
          <div class="report-card-icon">
            <svg viewBox="0 0 48 48" width="36" height="36" fill="none">
              <rect x="6" y="2" width="28" height="36" rx="3" fill="#e8f0fe" stroke="#409eff" stroke-width="2"/>
              <rect x="12" y="8" width="16" height="2" rx="1" fill="#409eff"/>
              <rect x="12" y="13" width="12" height="2" rx="1" fill="#b0c4de"/>
              <rect x="12" y="18" width="14" height="2" rx="1" fill="#b0c4de"/>
              <rect x="12" y="23" width="10" height="2" rx="1" fill="#b0c4de"/>
              <path d="M28 22 L38 22 L38 42 L10 42 L10 38" fill="#d4e6f1" stroke="#409eff" stroke-width="2"/>
              <rect x="14" y="28" width="12" height="8" rx="1" fill="#f0f5ff" stroke="#b0c4de" stroke-width="1"/>
            </svg>
          </div>
          <div class="report-card-info">
            <div class="report-card-title">{{ card.title }}</div>
            <div class="report-card-meta">
              <span>创建时间: {{ card.createTime }}</span>
            </div>
          </div>
          <div class="report-card-thumb">
            <div class="report-card-thumb-preview">
              <div class="thumb-line thumb-line-title"></div>
              <div class="thumb-line"></div>
              <div class="thumb-line thumb-line-short"></div>
            </div>
          </div>
          <div class="report-card-download">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#409eff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Token usage alert after assistant reply -->
    <el-alert
      v-if="msg.role === 'assistant' && msg.usage"
      class="usage-alert"
      type="info"
      :closable="false"
      show-icon
    >
      <template #title>
        Token 用量 - 提示词: {{ msg.usage.prompt_tokens ?? 0 }},
        完成: {{ msg.usage.completion_tokens ?? 0 }},
        总计: {{ msg.usage.total_tokens ?? 0 }}
      </template>
    </el-alert>

    <!-- truncatedByLength：撞 maxOutputTokens 的正常收尾。界面上完全没有异常感（流正常结束），
         不显式提示的话用户只会觉得"模型自己停了"，根本想不到还能继续 -->
    <div v-if="msg.role === 'assistant' && (msg.cancelled || msg.interrupted || msg.truncatedByLength) && !msg.streaming" class="interrupted-tip">
      <el-icon class="interrupted-ic"><Warning /></el-icon>
      <span class="interrupted-text">
        <template v-if="msg.truncatedByLength && !msg.cancelled && !msg.interrupted">
          回答已达单次输出长度上限<template v-if="msg.content"> · 已输出 {{ msg.content.length }} 字</template>，可继续生成
        </template>
        <template v-else>
          回答未生成完整<template v-if="msg.content"> · 已保留 {{ msg.content.length }} 字</template>
        </template>
      </span>
      <span class="interrupted-actions">
        <!-- 两个动作语义相反，必须并列给出：继续 = 从断点接着写（保留已生成内容）；
             重新生成 = 从头再答一遍（丢掉已生成内容）。只给一个的话，用户想要另一种就得自己想办法 -->
        <el-button link type="primary" size="small" @click="$emit('resume', msg)">继续生成</el-button>
        <el-button link type="info" size="small" @click="$emit('regenerate', msg)">重新生成</el-button>
      </span>
    </div>

    <!-- 朗读 AI 回复（TTS）：状态统一读全局 store，按 messageId 判定（不再用文本比对） -->
    <div v-if="msg.role === 'assistant' && !loading && msg.content" class="source-action">
      <el-button
        type="primary"
        size="small"
        text
        :class="{ 'is-speaking': isSpeaking }"
        :title="isSpeaking ? '停止朗读' : '朗读'"
        @click="$emit('speak', msg)"
      >
        <el-icon><Headset /></el-icon>
        <span v-if="isSpeaking && ttsStore.phase === 'synthesizing'">合成中</span>
        <span v-else-if="isSpeaking && ttsStore.phase === 'playing'">朗读中</span>
        <span v-else-if="isSpeaking && ttsStore.phase === 'paused'">已暂停</span>
        <span v-else>朗读</span>
      </el-button>
    </div>

    <!-- Source button for assistant messages with citations -->
    <div v-if="msg.role === 'assistant' && !loading && msg.sources && msg.sources.length > 0" class="source-action">
      <el-button
        type="primary"
        size="small"
        text
        @click="$emit('viewSource', msg.sources)"
      >
        <el-icon><Document /></el-icon>
        <span>查看来源 ({{ msg.sources.length }})</span>
      </el-button>
    </div>

    <!-- Minimal trace summary bar -->
    <div v-if="msg.role === 'assistant' && !loading && msg.knowledgeTraces && msg.knowledgeTraces.length > 0"
         class="trace-summary-bar" @click="$emit('viewTrace', msg)">
      <el-icon><Document /></el-icon>
      <span class="trace-summary-text">{{ msg.knowledgeTraces.length }}个知识来源</span>
      <span class="trace-separator">·</span>
      <span class="trace-confidence-dot" :class="'dot-' + (overallConfidenceLevel(msg))"></span>
      <span class="trace-summary-confidence">综合置信度 {{ (overallConfidenceValue(msg) * 100).toFixed(1) }}%</span>
      <span class="trace-view-detail">查看详情 -&gt;</span>
    </div>

    <!-- 命中文档图片（从知识溯源命中内容提取的文档内嵌图片，可点击放大预览） -->
    <div v-if="msg.role === 'assistant' && !loading && getHitImages(msg).length > 0" class="hit-images-section">
      <div class="hit-images-label">
        <el-icon><Document /></el-icon>
        <span>命中文档图片（{{ getHitImages(msg).length }}）</span>
      </div>
      <div class="hit-images-grid">
        <el-image
          v-for="(imgUrl, imgIdx) in getHitImages(msg)"
          :key="imgIdx"
          :src="imgUrl"
          :preview-src-list="getHitImages(msg)"
          :initial-index="imgIdx"
          fit="cover"
          class="hit-image"
          preview-teleported
        />
      </div>
    </div>
    <!-- 食台抓拍图片（AI 查到的最新食台记录图片，随回答展示，可点击放大） -->
    <div v-if="msg.role === 'assistant' && getFeedRecordImages(msg).length > 0" class="feed-images-section">
      <div class="feed-images-label">
        <el-icon><Document /></el-icon>
        <span>食台抓拍图片（{{ getFeedRecordImages(msg).length }}）</span>
      </div>
      <div class="feed-images-grid">
        <el-image
          v-for="(imgUrl, imgIdx) in getFeedRecordImages(msg)"
          :key="imgIdx"
          :src="imgUrl"
          :preview-src-list="getFeedRecordImages(msg)"
          :initial-index="imgIdx"
          fit="cover"
          class="feed-image"
          preview-teleported
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ArrowRight, Loading, ChatDotRound, Promotion, Headset, Document, CircleCheckFilled, CircleCloseFilled, Warning } from '@element-plus/icons-vue'
import {
  renderMessageHtml, getReportCards, openReportUrl, getWebSearchQuery,
  getHitImages, getFeedRecordImages, overallConfidenceLevel, overallConfidenceValue,
  formatToolResult, formatToolArguments, getToolDisplayName,
  isWebSearchExpanded,
  hasRunningStage, summarizeToolOutcome,
  buildTurnActions, turnCardSummary, isTurnCardExpanded
} from '@/modules/huyu-framework/backend/composables/useChatRender'
import useTtsStore from '@/store/modules/tts'
import useTtsHighlight from '@/modules/huyu-framework/backend/composables/useTtsHighlight'

const props = defineProps({
  msg: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  thinkingEnabled: { type: Boolean, default: false },
  // 该消息的澄清已由底部 V2 浮窗接管时隐藏内嵌澄清卡片（历史消息仍走内嵌卡片回看）
  hideClarify: { type: Boolean, default: false }
})

// 显式声明：续写/重新生成等动作都交给父级（父级掌握历史列表与发送管线）
defineEmits([
  'clarify', 'speak', 'viewSource', 'viewTrace',
  'toggleReasoning', 'toggleToolNode', 'toggleTurnCard', 'toggleWebSearch',
  'regenerate', 'resume'
])

// 朗读状态：全局 store 唯一来源，按消息 ID 判定当前是否为本条消息在朗读
const ttsStore = useTtsStore()
const isSpeaking = computed(() => ttsStore.isSpeaking(props.msg._id))

// ==================== 过程实时计时（等待有感知：进行中显示"已 Xs"） ====================
const nowTick = ref(Date.now())
let stageTickTimer = null
watch(
  () => hasRunningStage(props.msg) || !!(props.msg.toolChain && props.msg.toolChain.some(tc => tc.pending)),
  (busy) => {
    if (busy && !stageTickTimer) {
      stageTickTimer = setInterval(() => { nowTick.value = Date.now() }, 1000)
    } else if (!busy && stageTickTimer) {
      clearInterval(stageTickTimer)
      stageTickTimer = null
    }
  },
  { immediate: true }
)
onBeforeUnmount(() => {
  if (stageTickTimer) {
    clearInterval(stageTickTimer)
    stageTickTimer = null
  }
})
// "已 Xs"：兼容阶段（status==='running'）与工具节点（pending===true），均需 startAt
// （计时口径已收敛到 turnElapsed，这里不再单独算单个步骤）
// 句级高亮：把渲染后的内容容器交给 composable，按当前朗读句定位并高亮
const contentEl = ref(null)
useTtsHighlight(contentEl, () => props.msg._id)

// 可见告知：后端 node_message / 无选项 clarification 下发的整段话。
// 正文里已经包含的内容不再重复展示（拒答话术可能同时走 finalAnswer 与 node_message）。
const visibleNotices = computed(() => {
  const list = (props.msg && props.msg.notices) || []
  const body = (props.msg && props.msg.content) || ''
  return list.filter(n => n && !body.includes(n))
})

// ==================== 回合卡片（14 阶段归并 5 个动作）====================// 归并口径在 @huyu/core/ai/render.ts（buildTurnActions），此处只做状态派生与展示。
const turnActions = computed(() => buildTurnActions(props.msg).filter(a => a.visible))
const turnRunning = computed(() => turnActions.value.some(a => a.status === 'running'))
const turnFailed = computed(() => !turnRunning.value && turnActions.value.some(a => a.status === 'error'))
// 展开态：默认进行中展开、完成后收起；用户点击后以 msg._turnCardExpanded 为准
const turnExpanded = computed(() => isTurnCardExpanded(props.msg))
// 进行中整体已耗时：取最早一个 running 步骤的 startAt
const turnElapsed = computed(() => {
  if (!turnRunning.value) return ''
  const msg = props.msg || {}
  const stageStart = (msg.stages || []).filter(s => s.status === 'running').map(s => s.startAt).filter(Boolean)
  const toolStart = (msg.toolChain || []).filter(t => t.pending).map(t => t.startAt).filter(Boolean)
  const starts = [...stageStart, ...toolStart]
  if (!starts.length) return ''
  const s = Math.max(0, Math.round((nowTick.value - Math.min(...starts)) / 1000))
  return s > 0 ? `已 ${s}s` : ''
})

// 互联网搜索「参考资料」展开状态：isWebSearchExpanded 来自 useChatRender（读 msg._webSearchExpanded），
// 点击时 emit 给父级由 useChatRender.toggleWebSearchExpand 翻转状态并触发重渲染
</script>

<style scoped>
/* ===== 消息样式（左右气泡：用户消息右侧 / AI 消息左侧） ===== */
.message-item {
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
}

.message-item.user {
  background-color: #f0f7ff;
  align-self: flex-end;
  max-width: 75%;
  border: 1px solid #d6eaff;
}

/* AI 消息铺满消息区（过程面板/正文自适应左右，不随内容宽度抖动） */
.message-item.assistant {
  background-color: #fff;
  align-self: stretch;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.message-time {
  font-size: 12px;
  color: #8b949e;
}

.message-content {
  font-size: 14px;
  line-height: 1.7;
  color: #1f2328;
}

/* 用户消息中上传的图片缩略图 */
.user-message-image {
  display: block;
  max-width: 240px;
  max-height: 180px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 12px;
  object-fit: cover;
}

/* 多图（历史还原）：横向排列自动换行，间距由容器统一给，缩略图本身不再自带下边距 */
.user-message-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.user-message-images .user-message-image.is-grid {
  width: 112px;
  height: 112px;
  max-width: 112px;
  max-height: 112px;
  margin-bottom: 0;
}

.usage-alert {
  margin-top: 10px;
}

/* 中断态：不止说"已取消"，要交代「保留了多少」并给出下一步动作 */
.interrupted-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px dashed #e6a23c;
  border-radius: 7px;
  background: #fdf6ec;
  font-size: 12.5px;
  color: #b88230;
}

.interrupted-ic {
  font-size: 13px;
  flex-shrink: 0;
}

.interrupted-text {
  flex: 1;
}

.interrupted-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.clarification-card {
  margin-top: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}

.clarification-message {
  font-size: 14px;
  color: #1f2328;
  margin-bottom: 10px;
}

.clarification-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.clarification-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #1f2328;
  transition: all 0.15s;
}

.clarification-option:hover {
  border-color: #409eff;
  background: #f0f7ff;
}

.clarification-option .el-icon {
  color: #409eff;
}

.clarification-input-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.clarification-input-row .el-textarea {
  flex: 1;
}

.clarification-input-row .el-textarea__inner {
  font-size: 13px;
}

.clarification-input-row .el-button {
  flex-shrink: 0;
}

.source-action {
  margin-top: 8px;
}

/* ===== Markdown 渲染 ===== */
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
  color: #1f2328;
  font-weight: 600;
  line-height: 1.35;
  margin: 20px 0 12px;
}

.markdown-body :deep(h1) {
  font-size: 24px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.markdown-body :deep(h2) {
  font-size: 17px;
  padding: 0 0 0 12px;
  border-bottom: none;
  position: relative;
  margin: 24px 0 12px;
}

/* 章节标题左侧品牌渐变条（与结论卡/调用流同一视觉语言） */
.markdown-body :deep(h2)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #7c3aed, #4f8cff);
}

.markdown-body :deep(h3) {
  font-size: 17px;
}

.markdown-body :deep(p) {
  margin: 12px 0;
  line-height: 1.9;
  color: #1f2328;
  font-size: 15px;
}

.markdown-body :deep(strong) {
  font-weight: 600;
  color: #1f2328;
}

.markdown-body :deep(em) {
  color: #656d76;
}

.markdown-body :deep(a) {
  color: #409eff;
  text-decoration: none;
}

.markdown-body :deep(a:hover) {
  text-decoration: underline;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 24px;
  margin: 10px 0;
  line-height: 1.8;
  color: #1f2328;
}

.markdown-body :deep(li) {
  margin: 4px 0;
}

.markdown-body :deep(ul li::marker) {
  color: #8b949e;
}

.markdown-body :deep(ol li::marker) {
  color: #656d76;
  font-weight: 600;
}

.markdown-body :deep(blockquote) {
  border: 1px solid #c6e2ff;
  background: #f0f7ff;
  padding: 12px 16px;
  margin: 14px 0;
  border-radius: 10px;
  color: #1f2d3d;
  font-size: 15px;
  line-height: 1.8;
}

/* 引用块内的段落收紧（结论/警示块） */
.markdown-body :deep(blockquote p) {
  margin: 2px 0;
}

.markdown-body :deep(blockquote strong) {
  color: #1f2d3d;
}

.markdown-body :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
  background: #f6f8fa;
  border: 1px solid #eaeef2;
  border-radius: 4px;
  padding: 1px 6px;
  color: #1f2328;
}

.markdown-body :deep(pre) {
  background: #f6f8fa;
  border: 1px solid #eaeef2;
  border-radius: 8px;
  padding: 14px 16px;
  overflow-x: auto;
  margin: 12px 0;
  line-height: 1.6;
}

.markdown-body :deep(pre code) {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 14px;
  color: #1f2328;
}

.markdown-body :deep(table) {
  border-collapse: collapse;
  margin: 14px 0;
  width: 100%;
  font-size: 14px;
  color: #1f2328;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}

.markdown-body :deep(thead th) {
  background: #f6f8fa;
  font-weight: 600;
  text-align: left;
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
}

.markdown-body :deep(tbody td) {
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  border-right: 1px solid #ebeef5;
}

.markdown-body :deep(tbody td:last-child) {
  border-right: none;
}

.markdown-body :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.markdown-body :deep(tbody tr:hover td) {
  background: #fafbfc;
}

.markdown-body :deep(hr) {
  border: none;
  border-top: 1px dashed #ebeef5;
  margin: 26px 0;
}

.markdown-body :deep(img) {
  max-width: 100%;
  border-radius: 8px;
}

/* ===== 输入中 / 进度 ===== */
.typing-indicator-wrapper {
  padding: 8px 0;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background-color: #8b949e;
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #656d76;
  font-size: 13px;
}

.progress-icon {
  color: #409eff;
  animation: rotating 1s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.progress-node {
  font-weight: 500;
  color: #1f2328;
}

.progress-detail {
  color: #8b949e;
}

/* ===== 回合卡片（一条回答 = 一个卡片；14 阶段归并 5 个动作）===== */
.turn-card {
  margin: 2px 0 10px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #f8f9fb;
  overflow: hidden;
}

.turn-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  user-select: none;
}

.turn-head-icon {
  font-size: 14px;
  color: #67c23a;
  flex-shrink: 0;
}

.turn-head-icon.is-running {
  color: #409eff;
  animation: tool-pending-spin 1.1s linear infinite;
}

.turn-head-icon.is-fail {
  color: #f56c6c;
}

.turn-head-text {
  flex: 1;
  font-size: 12.5px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 实时计时（等待有感知："已 Xs"） */
.turn-head-elapsed {
  color: #409eff;
  font-weight: 400;
  font-size: 11.5px;
  margin-left: 2px;
}

.turn-head-arrow {
  font-size: 12px;
  color: #c0c4cc;
  transition: transform 0.18s;
}

.turn-head-arrow.is-expanded {
  transform: rotate(90deg);
}

.turn-card-body {
  padding: 2px 12px 9px;
}

/* 动作行：一个动作一行（理解问题 / 检索知识 / 调用工具 / 生成回答 / 收尾） */
.turn-action {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 3px 0;
  font-size: 12px;
}

.turn-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c0c4cc;
  flex-shrink: 0;
}

.turn-action.is-done .turn-dot {
  background: #67c23a;
}

.turn-action.is-running .turn-dot {
  background: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.18);
  animation: turn-dot-pulse 1.2s ease-in-out infinite;
}

.turn-action.is-error .turn-dot {
  background: #f56c6c;
}

@keyframes turn-dot-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

.turn-title {
  color: #606266;
  font-weight: 600;
  flex-shrink: 0;
}

.turn-action.is-running .turn-title {
  color: #409eff;
}

.turn-summary {
  color: #909399;
  font-size: 11.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.turn-action.is-running .turn-summary {
  color: #409eff;
}

.turn-dur {
  margin-left: auto;
  color: #c0c4cc;
  font-size: 11px;
  flex-shrink: 0;
}

/* 动作下的原始步骤：缩进一层，弱化显示 */
.turn-steps {
  flex-basis: 100%;
  margin: 2px 0 2px 14px;
  padding-left: 10px;
  border-left: 1px dashed #e4e7ed;
}

.turn-step {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 2px 0;
  font-size: 11.5px;
  color: #909399;
}

.turn-step.is-clickable {
  cursor: pointer;
}

.turn-step.is-clickable:hover {
  color: #409eff;
}

.ts-icon {
  font-size: 11px;
  flex-shrink: 0;
}

.ts-icon.is-pending {
  color: #409eff;
  animation: tool-pending-spin 1.1s linear infinite;
}

.ts-icon.is-fail {
  color: #f56c6c;
}

.ts-icon-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #67c23a;
  flex-shrink: 0;
}

.turn-step.is-running .ts-icon-dot {
  background: #409eff;
}

.ts-label {
  flex-shrink: 0;
}

.ts-detail {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
  color: #a8abb2;
}

.ts-dur {
  margin-left: auto;
  color: #c0c4cc;
  flex-shrink: 0;
}

/* node_message 整段告知（如知识库拒答说明） */
.msg-notices {
  margin: 4px 0 10px;
  padding: 10px 14px;
  border-left: 3px solid #e6a23c;
  background: #fdf6ec;
  border-radius: 0 8px 8px 0;
}

.msg-notice {
  font-size: 13px;
  color: #606266;
  line-height: 1.75;
  white-space: pre-wrap;
}

.msg-notice + .msg-notice {
  margin-top: 6px;
}

.loop-reasoning {
  padding: 8px 12px 4px;
}

.loop-reasoning-head {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.loop-reasoning-label {
  font-size: 12px;
  font-weight: 600;
  color: #656d76;
}

.loop-reasoning-arrow {
  margin-left: auto;
  font-size: 12px;
  color: #8b949e;
  transition: transform 0.2s;
}

.loop-reasoning-arrow.is-expanded {
  transform: rotate(90deg);
}

.loop-reasoning-text {
  margin-top: 6px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #656d76;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow-y: auto;
  background: #f6f8fa;
  border-radius: 6px;
}

.tool-chain {
  padding: 4px 12px 10px;
}

/* ===== 工具调用明细（挂在回合卡片的「调用工具」动作下，点击某次调用展开） ===== */
.tf-detail {
  padding: 8px 14px 12px;
  border-top: 1px dashed #f0f2f5;
  background: #fcfcfd;
}

/* 进行中的工具调用节点（progress 事件驱动） */
.tool-chain-pending {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 2px 12px 10px;
  padding: 6px 10px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  background: #fdfdfd;
  color: #606266;
  font-size: 12.5px;
}

.tool-chain-pending .progress-icon {
  color: #409eff;
  animation: tool-pending-spin 1.1s linear infinite;
}

@keyframes tool-pending-spin {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

.tool-chain-group {
  position: relative;
}

.tool-chain-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  cursor: pointer;
  border-radius: 8px;
  background: #f6f8fa;
  border: 1px solid #ebeef5;
  user-select: none;
  transition: background 0.2s;
}

.tool-chain-group-head:hover {
  background: #ecf5ff;
}

.tool-chain-group-icon {
  color: #409eff;
  font-size: 15px;
}

.tool-chain-group-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2328;
}

.tool-chain-group-count {
  font-size: 12px;
  color: #8b949e;
  background: #f0f2f5;
  border-radius: 10px;
  padding: 1px 8px;
}

.tool-chain-group-body {
  padding: 0 0 2px 11px;
  border-left: 1px solid #ebeef5;
  margin-left: 11px;
}

.tool-chain-item {
  position: relative;
}

.tool-chain-node {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  background: #fff;
  transition: all 0.15s;
}

.tool-chain-node:hover {
  background: #f6f8fa;
}

/* 进行中的工具节点：浅蓝底高亮 + 左侧呼吸边（执行过程实时可见） */
.tool-chain-node.is-pending {
  background: #f0f7ff;
  border-color: #c6e2ff;
}

/* 状态图标：成功绿 / 失败红 / 进行中蓝旋转 */
.tool-chain-status {
  font-size: 15px;
  flex-shrink: 0;
}

.tool-chain-status.is-ok {
  color: #67c23a;
}

.tool-chain-status.is-fail {
  color: #f56c6c;
}

.tool-chain-status.is-pending {
  color: #409eff;
  animation: tool-pending-spin 1.1s linear infinite;
}

.tool-chain-duration.is-pending-text {
  color: #409eff;
}

.tool-chain-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #8b949e;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.tool-chain-name {
  font-size: 13px;
  font-weight: 500;
  color: #1f2328;
}

.tool-chain-duration {
  font-size: 12px;
  color: #8b949e;
}

.tool-chain-node-arrow {
  margin-left: auto;
  font-size: 12px;
  color: #8b949e;
  transition: transform 0.2s;
}

.tool-chain-node-arrow.is-expanded {
  transform: rotate(90deg);
}

.tool-chain-detail {
  margin: 4px 0 4px 30px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #f6f8fa;
  overflow: hidden;
}

.tool-chain-link {
  display: block;
  width: 2px;
  height: 12px;
  margin: 0 auto;
  background: #d0d7de;
  position: relative;
}

.tool-chain-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #d0d7de;
}

.flow-block {
  padding: 6px 10px;
}

.flow-block + .flow-block {
  border-top: 1px dashed #ebeef5;
}

.flow-block-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #656d76;
  margin-bottom: 4px;
}

.reasoning-rounds {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.reasoning-round {
  border: 1px solid #ebeef5;
  border-left: 3px solid #8b949e;
  border-radius: 8px;
  background: #f6f8fa;
  padding: 6px 10px;
}

.reasoning-round-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.reasoning-round-badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #656d76;
  background: #f0f2f5;
  border-radius: 4px;
  padding: 1px 6px;
}

.reasoning-round-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2328;
}

.reasoning-round-content {
  font-size: 13px;
  line-height: 1.6;
  color: #656d76;
  white-space: pre-wrap;
  word-break: break-word;
}

.flow-block-code {
  margin: 0;
  padding: 8px;
  max-height: 180px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #1f2328;
  background: #f6f8fa;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ===== Web 搜索 ===== */
.web-search-section {
  margin-bottom: 12px;
  padding: 0;
}

.web-search-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #656d76;
  cursor: pointer;
  user-select: none;
}

.web-search-toggle {
  transition: transform 0.2s ease;
  display: inline-flex;
  align-items: center;
  color: #656d76;
  transform: rotate(0deg);
}

.web-search-toggle.is-expanded {
  transform: rotate(90deg);
}

.web-search-keyword {
  color: #656d76;
  font-size: 14px;
}

.web-search-query {
  font-size: 14px;
  color: #656d76;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.web-search-refs {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.web-search-ref-item {
  line-height: 1.6;
}

.web-search-ref-link {
  font-size: 14px;
  color: #409eff;
  text-decoration: none;
  display: inline;
}

.web-search-ref-link:hover {
  text-decoration: underline;
}

.web-search-ref-index {
  color: #656d76;
  font-size: 14px;
  margin-right: 4px;
}

.web-search-divider {
  margin-top: 12px;
  border-top: 1px dashed #ebeef5;
}

.web-search-details {
  overflow: hidden;
}

.web-search-expand-enter-active,
.web-search-expand-leave-active {
  transition: all 0.2s ease;
  max-height: 500px;
  opacity: 1;
}

.web-search-expand-enter-from,
.web-search-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ===== 报告卡片 ===== */
.report-cards-section {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.report-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.report-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.report-card-body {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.report-card-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f6f8fa;
  border-radius: 10px;
}

.report-card-info {
  flex: 1;
  min-width: 0;
}

.report-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2328;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-card-meta {
  font-size: 12px;
  color: #8b949e;
  margin-top: 2px;
}

.report-card-thumb {
  flex-shrink: 0;
  width: 56px;
  height: 68px;
  background: #f6f8fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 6px;
  display: flex;
  align-items: flex-start;
}

.report-card-thumb-preview {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
}

.thumb-line {
  height: 3px;
  background: #d0d7de;
  border-radius: 1px;
  width: 100%;
}

.thumb-line-title {
  height: 4px;
  background: #8b949e;
  width: 70%;
}

.thumb-line-short {
  width: 55%;
}

.report-card-download {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  transition: background 0.2s;
}

.report-card:hover .report-card-download {
  background: #f6f8fa;
}

/* ===== 溯源摘要 ===== */
.trace-summary-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  background: #f6f8fa;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #656d76;
  transition: all 0.2s;
  border: 1px solid #ebeef5;
}

.trace-summary-bar:hover {
  background: #ecf5ff;
}

.trace-summary-text {
  font-weight: 600;
  color: #1f2328;
}

.trace-separator {
  color: #8b949e;
}

.trace-confidence-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.dot-high {
  background: #3fb950;
}

.dot-medium {
  background: #d29922;
}

.dot-low {
  background: #cf222e;
}

.trace-summary-confidence {
  color: #656d76;
}

.trace-view-detail {
  margin-left: auto;
  font-weight: 500;
}

/* ===== 命中文档图片 ===== */
.hit-images-section {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.hit-images-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.hit-images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.hit-image {
  width: 120px;
  height: 90px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  cursor: zoom-in;
  flex-shrink: 0;
  background: #fff;
}

/* ===== 食台抓拍图片 ===== */
.feed-images-section {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d9ecff;
  border-radius: 8px;
}

.feed-images-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #409eff;
  margin-bottom: 6px;
}

.feed-images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feed-image {
  display: block;
  width: 200px;
  max-width: 100%;
  height: 140px;
  border-radius: 6px;
  border: 1px solid #e6eef8;
  cursor: zoom-in;
  flex-shrink: 0;
  background: #f6f8fa;
}

/* ===== 引用角标（v-html 注入，需 :deep 穿透 scoped） ===== */
:deep(.citation-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  margin: 0 1px;
  vertical-align: super;
  transition: all 0.15s ease;
  border: 1px solid #b3d8ff;
  user-select: none;
  font-style: normal;
}

:deep(.citation-badge:hover) {
  background: #409eff;
  color: #fff;
  border-color: #409eff;
  transform: translateY(-1px);
}

:deep(.citation-badge.citation-high) {
  background: #f6f8fa;
  color: #3fb950;
  border-color: #3fb950;
}

:deep(.citation-badge.citation-high:hover) {
  background: #3fb950;
  color: #fff;
  border-color: #3fb950;
}

:deep(.citation-badge.citation-medium) {
  background: #f6f8fa;
  color: #d29922;
  border-color: #d29922;
}

:deep(.citation-badge.citation-medium:hover) {
  background: #d29922;
  color: #fff;
  border-color: #d29922;
}

:deep(.citation-badge.citation-low) {
  background: #f6f8fa;
  color: #cf222e;
  border-color: #cf222e;
}

:deep(.citation-badge.citation-low:hover) {
  background: #cf222e;
  color: #fff;
  border-color: #cf222e;
}
</style>
